import { useQuery } from '@tanstack/react-query';
import { ErrorCodes } from 'errors';

import { FinCENApi } from 'api';
import { ApiError } from 'api/api-util/api-error';
import { FINCEN_REPORT_KEY } from 'hooks/keys';
import { useUser } from 'hooks/queries';

/**
 * A single FinCEN report by id. Used to resolve the prior SAR in a chain (via `previous_sar_id_link`)
 * so its `filed_on` date can populate the SAR narrative's Prior SAR Filing Date token. Disabled until
 * both the bank id and a report id are known; cached so the prior report is fetched once.
 *
 * An orphaned reference (id no longer resolves to a record) is an expected soft condition — it
 * resolves to `null` so consumers can degrade gracefully (e.g. show N/A) instead of firing the
 * "contact support" snackbar. Genuine failures still surface via `snackbarErrorMessage`.
 */
export function useFincenReportById(reportId: string | undefined) {
  const { userBankId } = useUser();
  const fincenApi = new FinCENApi();

  const { data: fincenReport, isLoading: isLoadingFincenReport } = useQuery({
    enabled: !!userBankId.length && !!reportId,
    queryKey: [FINCEN_REPORT_KEY, userBankId, reportId],
    queryFn: async () => {
      try {
        const report = await fincenApi.getReportById(userBankId, reportId as string);
        // Coalesce a missing record (empty result) to null — react-query rejects undefined.
        return report ?? null;
      } catch (error) {
        if (error instanceof ApiError && error.code === ErrorCodes.NotFound) {
          return null;
        }
        throw error;
      }
    },
    meta: {
      snackbarErrorMessage: 'There was an issue loading the prior FinCEN report. Please contact support.'
    }
  });

  return { fincenReport, isLoadingFincenReport };
}
